import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useLocation, useRouteLoaderData } from '@remix-run/react';
import type { PostTheme, WebTheme } from '@shared/dream-components';
import { PaywallElement } from '@shared/dream-components';

import Text from '~/components/Text';
import usePodcastEpisodePaywallEffects from '~/hooks/usePodcastEpisodePaywallEffects';
import type { RootLoaderData } from '~/root';
import { getContrastingColor, hasMinimumContrast } from '~/utils/getContrastingColor';

const GRADIENT_HEIGHT_PX = 120;

type Props = {
  primaryTheme?: WebTheme;
  backgroundColor: string;
};

const PodcastEpisodePaywall = ({ primaryTheme, backgroundColor }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const subscription = rootData?.subscription;
  const offeringName = rootData?.site?.publication?.name || '';
  const containerRef = useRef<HTMLDivElement>(null);

  const [isPlaced, setIsPlaced] = useState(false);
  const handlePlaced = useCallback(() => setIsPlaced(true), []);
  usePodcastEpisodePaywallEffects(containerRef, handlePlaced);

  const upgradeUrl = useMemo(() => {
    const search = new URLSearchParams(location.search).toString();
    return `/upgrade${search ? '?' : ''}${search}`;
  }, [location.search]);

  // From the router, not window, so the server and client render the same href.
  const loginPath = `/login?postSlug=${encodeURIComponent(location.pathname)}`;

  const theme = useMemo((): PostTheme | undefined => {
    if (!primaryTheme) return undefined;

    return {
      paragraph_font_family: primaryTheme.body_font,
      paragraph_font_size: '16',
      paragraph_line_height_size: 'Normal',
      h2_font_family: primaryTheme.header_font,
      h2_font_size: '30',
      h2_line_height_size: 'Normal',
      h4_font_family: primaryTheme.header_font,
      h4_font_size: '20',
      h4_line_height_size: 'Normal',
      button_font_family: primaryTheme.button_font,
      button_border_radius: primaryTheme.border_radius,
      button_padding_top: '12',
      button_padding_bottom: '24',
      quote_3_mark_color: '',
      quote_3_mark_alignment: '',
      quote_3_mark_spacing: '',
      quote_3_mark_font_size: '',
      quote_3_mark_font_family: '',
    };
  }, [primaryTheme]);

  const paywallStyle = useMemo(() => {
    const buttonBackgroundColor = primaryTheme?.primary_color || '#000000';
    const contrastingColor = getContrastingColor(buttonBackgroundColor);
    const buttonTextColor =
      [
        primaryTheme?.text_on_primary_color,
        contrastingColor,
        contrastingColor === '#FFFFFF' ? '#000000' : '#FFFFFF',
      ].find((color) => hasMinimumContrast(color, buttonBackgroundColor)) ?? contrastingColor;

    return {
      show_image: false,
      background_color: backgroundColor,
      text_color: primaryTheme?.text_on_background_color || '#000000',
      button_background_color: buttonBackgroundColor,
      button_text_color: buttonTextColor,
      container_shadow: '0 0 #00000000',
    };
  }, [backgroundColor, primaryTheme]);

  return (
    <div
      ref={containerRef}
      className={`pointer-events-none absolute inset-x-0 z-10 ${isPlaced ? '' : 'podcast-episode-paywall-pending'}`}
      style={{ backgroundColor }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0"
        style={{
          top: -GRADIENT_HEIGHT_PX,
          height: GRADIENT_HEIGHT_PX,
          background: `linear-gradient(to bottom, transparent 0%, ${backgroundColor} 100%)`,
        }}
      />
      <div className="pointer-events-auto mx-auto max-w-xl space-y-4 px-6 pb-12 pt-4">
        <PaywallElement
          theme={theme}
          upgradeUrl={upgradeUrl}
          upgradePath="/upgrade"
          shouldUseUrl
          isLive
          element={{
            type: 'paywall_break',
            attrs: {
              id: 'podcast-episode-paywall',
              data: {
                title: t('podcasts.episode.paywall.title'),
                description: t('podcasts.episode.paywall.description', { name: offeringName }),
                cta_text: t('podcasts.episode.paywall.cta'),
                benefits: [],
                subheader: '',
                style: paywallStyle,
              },
            },
            content: [{ id: 'text', text: '' }],
          }}
        >
          {null}
        </PaywallElement>

        {!subscription && (
          <Text
            as="span"
            type="body"
            theme="content"
            size="xs"
            className="flex items-center justify-center text-center text-xs tracking-wide opacity-75 sm:text-lg"
          >
            {t('login.already_a_subscriber')}
            <Link to={loginPath} className="ml-1">
              <span className="text-wt-primary underline underline-offset-1">{t('shared.cta.sign_in')}</span>.
            </Link>
          </Text>
        )}
      </div>
    </div>
  );
};

export default PodcastEpisodePaywall;
